import { useCallback, useEffect, useState } from "react";
import { getRaw } from "../../api/apiShared";
import { downloadFile, saveBlobAsFile } from "../../api/downloadFile";
import { DocumentFileType, IDocumentData, IDocumentNavigation, PreviewableDocument } from "./DocumentPreview.types";
import { useQuery } from "@tanstack/react-query";

interface IUseDocumentPreviewProps {
    open: boolean;
    currentDocument: PreviewableDocument | undefined;
}

function getDocumentUrl(document: PreviewableDocument): string {
    if (document.customDocumentUrl) return document.customDocumentUrl;

    if (document.documentVersionId) {
        return `/document/${document.documentId}/${document.documentVersionId}/data-from-version/${document.entityType}/${document.entityId}`;
    }
    return `/document/${document.documentId}/${document.dataId}/data/${document.entityType}/${document.entityId}`;
}

export function getExtractedTextUrl(document: PreviewableDocument): string {
    return `/document/${document.documentId}/${document.documentVersionId}/extracted-text/${document.entityType}/${document.entityId}`;
}

export function detectFileType(fileName: string | undefined): DocumentFileType {
    if (!fileName) {
        return DocumentFileType.Unsupported;
    }
    const extension = fileName.split(".").pop()?.toLowerCase();

    if (extension === "pdf") {
        return DocumentFileType.Pdf;
    }

    const imageExtensions = ["png", "jpg", "jpeg", "gif", "bmp", "webp"];
    if (extension && imageExtensions.includes(extension)) {
        return DocumentFileType.Image;
    }

    if (extension === "docx") {
        return DocumentFileType.Word;
    }

    const emailExtensions = ["msg", "eml"];
    if (extension && emailExtensions.includes(extension)) {
        return DocumentFileType.Email;
    }

    return DocumentFileType.Unsupported;
}

export function isPreviewSupported(fileName: string): boolean {
    const fileType = detectFileType(fileName);
    return fileType !== DocumentFileType.Unsupported;
}

/**
 * Whether the viewer this file lands in can read a text selection out of what it renders. Only the pdf,
 * word and extracted-text views mount the selection layer; an image has no text, and an email renders in
 * a sandboxed iframe the host document's selection cannot reach. The text view is the scanned-pdf
 * fallback, so the pdf type covers it.
 */
export function supportsTextSelection(fileName: string): boolean {
    const fileType = detectFileType(fileName);
    return fileType === DocumentFileType.Pdf || fileType === DocumentFileType.Word;
}

async function fetchDocumentBlob(url: string, fileName: string, fileType: DocumentFileType): Promise<IDocumentData> {
    const response = await getRaw(url);

    if (response.status === 200) {
        return toDocumentData(await response.blob(), fileName, fileType);
    }

    throw new Error("Failed to load document");
}

async function toDocumentData(blob: Blob, fileName: string, fileType: DocumentFileType): Promise<IDocumentData> {
    const needsArrayBuffer = fileType === DocumentFileType.Word || fileType === DocumentFileType.Email;

    return {
        blobUrl: window.URL.createObjectURL(blob),
        arrayBuffer: needsArrayBuffer ? await blob.arrayBuffer() : null,
        fileName,
        fileType,
    };
}

const ZOOM_LEVELS = [0.25, 0.5, 0.75, 1, 1.25, 1.5, 1.75, 2, 2.25, 2.5, 2.75, 3];
const MIN_ZOOM = ZOOM_LEVELS[0];
const MAX_ZOOM = ZOOM_LEVELS[ZOOM_LEVELS.length - 1];

export function useDocumentKeyboardNavigation(documentNavigation: IDocumentNavigation | undefined) {
    useEffect(() => {
        if (!documentNavigation || documentNavigation.totalDocuments <= 1) return;

        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === "ArrowLeft") {
                documentNavigation.onPreviousDocument();
            } else if (event.key === "ArrowRight") {
                documentNavigation.onNextDocument();
            }
        };

        document.addEventListener("keydown", handleKeyDown);
        return () => document.removeEventListener("keydown", handleKeyDown);
    }, [documentNavigation]);
}

export function useDocumentPreview(props: IUseDocumentPreviewProps) {
    const [zoomLevel, setZoomLevel] = useState(1);
    const [zoomInputValue, setZoomInputValue] = useState("100");
    const [isDownloading, setIsDownloading] = useState(false);

    const localFile = props.currentDocument?.localFile;
    const documentUrl = props.currentDocument && !localFile ? getDocumentUrl(props.currentDocument) : "";
    const documentName = props.currentDocument?.fileName ?? "";

    const fileType = detectFileType(documentName);
    const queryKey = localFile ? `document-preview-local-${props.currentDocument!.documentId}` : `document-preview-${documentUrl}`;

    const {
        isLoading,
        data: documentData,
        error,
    } = useQuery({
        queryKey: [queryKey],
        queryFn: () => (localFile ? toDocumentData(localFile, documentName, fileType) : fetchDocumentBlob(documentUrl, documentName, fileType)),
        enabled: props.open && !!props.currentDocument,
        staleTime: 5 * 60 * 1000,
        gcTime: 5 * 60 * 1000,
    });

    useEffect(() => {
        if (!props.open) {
            setZoomLevel(1);
            setZoomInputValue("100");
        }
    }, [props.open]);

    useEffect(() => {
        setZoomLevel(1);
        setZoomInputValue("100");
    }, [props.currentDocument]);

    const applyZoomLevel = useCallback((newZoom: number) => {
        const clampedZoom = Math.max(MIN_ZOOM, Math.min(newZoom, MAX_ZOOM));
        setZoomLevel(clampedZoom);
        setZoomInputValue(String(Math.round(clampedZoom * 100)));
    }, []);

    const handleZoomIn = useCallback(() => {
        setZoomLevel((prev) => {
            const nextLevel = ZOOM_LEVELS.find((level) => level > prev);
            const newZoom = nextLevel ?? MAX_ZOOM;
            setZoomInputValue(String(Math.round(newZoom * 100)));
            return newZoom;
        });
    }, []);

    const handleZoomOut = useCallback(() => {
        setZoomLevel((prev) => {
            const previousLevels = ZOOM_LEVELS.filter((level) => level < prev);
            const newZoom = previousLevels.length > 0 ? previousLevels[previousLevels.length - 1] : MIN_ZOOM;
            setZoomInputValue(String(Math.round(newZoom * 100)));
            return newZoom;
        });
    }, []);

    // Ctrl+scroll and trackpad pinch report a factor rather than a level, so they compound from wherever the
    // zoom currently sits and land on the same value the toolbar shows.
    const handleZoomBy = useCallback(
        (factor: number) => {
            applyZoomLevel(zoomLevel * factor);
        },
        [zoomLevel, applyZoomLevel]
    );

    const handleZoomInputChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
        setZoomInputValue(event.target.value);
    }, []);

    const handleZoomInputKeyDown = useCallback(
        (event: React.KeyboardEvent<HTMLInputElement>) => {
            if (event.key === "Enter") {
                const percentage = parseInt(zoomInputValue, 10);
                if (!isNaN(percentage)) {
                    applyZoomLevel(percentage / 100);
                }
            }
        },
        [zoomInputValue, applyZoomLevel]
    );

    const handleZoomInputBlur = useCallback(() => {
        const percentage = parseInt(zoomInputValue, 10);
        if (!isNaN(percentage)) {
            applyZoomLevel(percentage / 100);
        } else {
            setZoomInputValue(String(Math.round(zoomLevel * 100)));
        }
    }, [zoomInputValue, zoomLevel, applyZoomLevel]);

    const handleDownload = useCallback(async () => {
        if (!props.currentDocument) return;
        if (localFile) {
            saveBlobAsFile(localFile, documentName);
            return;
        }
        setIsDownloading(true);
        await downloadFile(documentUrl, documentName);
        setIsDownloading(false);
    }, [props.currentDocument, localFile, documentUrl, documentName]);

    return {
        documentData,
        isLoading,
        error,
        fileType,
        zoomLevel,
        zoomInputValue,
        isDownloading,
        handleZoomIn,
        handleZoomBy,
        handleZoomOut,
        handleZoomInputChange,
        handleZoomInputKeyDown,
        handleZoomInputBlur,
        handleDownload,
    };
}

// What the toolbar needs to lay out on a single row: zoom, the page selector, download, the 400px search
// field with its match counter beside it, the dividers between those groups and both side groups. Measured
// from the widest arrangement, so the row it protects is the one that has the most in it.
const TOOLBAR_SINGLE_ROW_MINIMUM_WIDTH = 1100;

/**
 * Whether the toolbar has to give up its single-row form. Driven by the toolbar's own width rather than by
 * which slot its host occupies, so it follows a rail drag continuously and is equally right for a dialog on
 * a small screen. The threshold is a constant, so shrinking the compact form cannot widen the toolbar back
 * past it and set the two forms oscillating.
 */
export function useIsToolbarCompact(elementRef: React.RefObject<HTMLElement | null>) {
    const [isCompact, setIsCompact] = useState(false);

    useEffect(() => {
        const element = elementRef.current;
        if (!element) return;

        const resizeObserver = new ResizeObserver((entries) => {
            const width = entries[0]?.contentRect.width;
            if (width !== undefined) {
                setIsCompact(width < TOOLBAR_SINGLE_ROW_MINIMUM_WIDTH);
            }
        });

        resizeObserver.observe(element);
        return () => resizeObserver.disconnect();
    }, [elementRef]);

    return isCompact;
}
